import React, { useRef, useEffect, useState, useCallback } from "react";
import DroneDashHelp from "./components/DroneDashHelp";

const LANES = 3;
const DRONE_Y_RATIO = 0.86;

const FACTS = [
  "Consumer drones are usually capped around 120m altitude.",
  "Delivery drones can cut a short trip to minutes.",
  "Some drones can fly for over an hour on one charge.",
  "Farmers use drones to check crop health from the air.",
];
const WOMEN_MSGS = [
  "A handloom scarf, woven by a women's collective.",
  "Pottery from a village women's self-help group.",
  "Pickle jars, home-made by a local women-run kitchen.",
];
const RELIEF_MSGS = [
  "Your relief drone never carries weapons — only supplies.",
  "Getting this parcel through safely matters more here.",
  "Satellites overhead track safe corridors for relief flights.",
];

const DIFF = {
  easy: {
    drain: 0.08,
    spawnBase: 70,
    speedBase: 1.8,
    speedRamp: 0.02,
    fatigueLimit: 260,
    fatiguePenalty: 2,
    hitPenalty: 10,
    bigHitExtra: 6,
    sessionCapSec: 90,
    missionBonus: 150,
    allowConflict: false,
  },
  medium: {
    drain: 0.12,
    spawnBase: 55,
    speedBase: 2.2,
    speedRamp: 0.03,
    fatigueLimit: 180,
    fatiguePenalty: 3,
    hitPenalty: 12,
    bigHitExtra: 6,
    sessionCapSec: 90,
    missionBonus: 200,
    allowConflict: true,
  },
  hard: {
    drain: 0.16,
    spawnBase: 42,
    speedBase: 2.6,
    speedRamp: 0.045,
    fatigueLimit: 130,
    fatiguePenalty: 4,
    hitPenalty: 16,
    bigHitExtra: 6,
    sessionCapSec: 90,
    missionBonus: 250,
    allowConflict: true,
  },
};

const PEACE_OBSTACLES = [
  { icon: "🐦", weight: 5, speedMul: 1, tele: 0 },
  { icon: "⚡", weight: 3, speedMul: 1, tele: 0, pair: true },
  { icon: "🏢", weight: 1, speedMul: 0.8, tele: 0 },
  { icon: "🛸", weight: 1, speedMul: 1, tele: 0 },
];
const CONFLICT_OBSTACLES = [
  { icon: "🐦", weight: 2, speedMul: 1, tele: 0 },
  { icon: "🚀", weight: 4, speedMul: 1.8, tele: 22 },
  { icon: "🛸", weight: 3, speedMul: 1, tele: 0, swarm: true },
  { icon: "🛰", weight: 1, speedMul: 0.6, tele: 0, big: true },
];

const PEACE_PICKUPS = [
  { icon: "🔋", kind: "battery", weight: 22 },
  { icon: "🍬", kind: "toffee", weight: 18, points: 30, color: "#D4537E" },
  { icon: "🍎", kind: "food", weight: 16, points: 40, color: "#EF9F27" },
  { icon: "🥕", kind: "food", weight: 10, points: 40, color: "#EF9F27" },
  { icon: "🧺", kind: "agri", weight: 12, points: 60, color: "#639922" },
  { icon: "🌾", kind: "agri", weight: 8, points: 60, color: "#639922" },
  { icon: "🧶", kind: "women", weight: 8, points: 80, color: "#7F77DD" },
  { icon: "🏺", kind: "women", weight: 5, points: 80, color: "#7F77DD" },
  { icon: "💡", kind: "fact", weight: 6, points: 20, color: "#EF9F27" },
  { icon: "🛡", kind: "shield", weight: 5, points: 0, color: "#378ADD" },
];
const CONFLICT_PICKUPS = PEACE_PICKUPS.concat([
  { icon: "📡", kind: "radar", weight: 10, points: 0, color: "#378ADD" },
]);

function pickWeighted(pool) {
  const total = pool.reduce((a, k) => a + k.weight, 0);
  let r = Math.random() * total;
  for (const k of pool) {
    r -= k.weight;
    if (r <= 0) return k;
  }
  return pool[0];
}

// ── Sound (Web Audio oscillators — no audio files, stays lightweight) ──
let sharedAudioCtx = null;
function getAudioCtx() {
  if (!sharedAudioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    sharedAudioCtx = new Ctx();
  }
  if (sharedAudioCtx.state === "suspended") sharedAudioCtx.resume();
  return sharedAudioCtx;
}
function playTone(freq, duration, type = "sine", vol = 0.15, delay = 0) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.value = vol;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const start = ctx.currentTime + delay;
    osc.start(start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
    osc.stop(start + duration);
  } catch (e) {
    /* ignore — sound is a nice-to-have, never block gameplay on it */
  }
}
const SOUND = {
  battery: () => playTone(660, 0.14, "sine", 0.15),
  toffee: () => playTone(880, 0.1, "square", 0.1),
  food: () => playTone(760, 0.1, "square", 0.1),
  agri: () => playTone(700, 0.12, "triangle", 0.12),
  women: () => { playTone(700, 0.1, "triangle", 0.12); playTone(950, 0.14, "triangle", 0.1, 0.08); },
  fact: () => { playTone(600, 0.1, "sine", 0.1); playTone(900, 0.14, "sine", 0.1, 0.09); },
  shieldUp: () => playTone(520, 0.18, "triangle", 0.14),
  shieldUsed: () => playTone(300, 0.2, "triangle", 0.14),
  nearMiss: () => playTone(1100, 0.06, "square", 0.07),
  hit: () => playTone(120, 0.25, "sawtooth", 0.18),
  restless: () => playTone(160, 0.15, "sawtooth", 0.1),
  gust: () => playTone(220, 0.3, "sine", 0.08),
  takeoff: () => { playTone(320, 0.08, "sine", 0.12); playTone(520, 0.08, "sine", 0.12, 0.08); playTone(720, 0.1, "sine", 0.12, 0.16); },
  gameover: () => { playTone(300, 0.15, "sawtooth", 0.14); playTone(220, 0.18, "sawtooth", 0.14, 0.14); playTone(140, 0.28, "sawtooth", 0.14, 0.3); },
};
function play(kind, muted) {
  if (muted) return;
  SOUND[kind] && SOUND[kind]();
}

function addParticles(g, x, y, color, count = 8) {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1 + Math.random() * 2.2;
    g.particles.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, alpha: 1, color });
  }
}

// ── Practice-mode hint: "what should I do right now?" ──
function computeHint(g, droneY, LOOKAHEAD) {
  const dangerLanes = new Set();
  g.obstacles.forEach((o) => {
    if (o.teleLeft > 0 || o.hit) return;
    if (o.y < droneY && droneY - o.y < LOOKAHEAD) dangerLanes.add(o.lane);
  });
  if (!dangerLanes.has(g.lane)) return { text: "✅ Stay put", speak: "stay" };
  const safeLanes = [0, 1, 2].filter((l) => l !== g.lane && !dangerLanes.has(l));
  if (safeLanes.length === 0) return { text: "⚠ Brace — no clear lane!", speak: "watch out" };
  return safeLanes[0] < g.lane
    ? { text: "⬅ Move left", speak: "left" }
    : { text: "➡ Move right", speak: "right" };
}
function speakHint(text) {
  if (!("speechSynthesis" in window)) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.1;
    u.volume = 0.8;
    window.speechSynthesis.speak(u);
  } catch (e) {
    /* speech is a bonus, never block gameplay */
  }
}

export default function DroneDash({
  difficulty = "medium",
  mode = "peace",
  onGameOver,
  onExit,
}) {

  const gv = useRef({
    lane: 1,
    displayX: 0,
    battery: 100,
    score: 0,
    distance: 0,
    combo: 0,
    packagesCollected: 0,
    speed: DIFF[difficulty]?.speedBase ?? DIFF.medium.speedBase,
    spawnTimer: 0,
    spawnEvery: DIFF[difficulty]?.spawnBase ?? DIFF.medium.spawnBase,
    obstacles: [],
    pickups: [],
    popups: [],
    particles: [],
    lastGust: 0,
    shield: false,
    shake: 0,
    rotorAngle: 0,
    laneTimer: 0,
    startedAt: Date.now(),
  });

  const [showDroneDashHelp, setShowDroneDashHelp] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  const overRef = useRef(false);
  const runningRef = useRef(false);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const practiceRef = useRef(false);
  const [hintText, setHintText] = useState("");
  const lastHintRef = useRef("");
  const lastSpokenAtRef = useRef(0);
  const touchStartX = useRef(null);
  const [hud, setHud] = useState({
    score: 0,
    battery: 100,
    running: false,
    over: false,
    msg: "",
  });

  const W = 320,
    H = 440;
  const laneW = W / LANES;
  const droneY = H * DRONE_Y_RATIO;
  const laneX = (l) => l * laneW + laneW / 2;

  const diff = DIFF[difficulty] || DIFF.medium;
  const safeMode = mode === "conflict" && !diff.allowConflict ? "peace" : mode;

  const flashMsg = useCallback((text, ms = 2000) => {
    setHud((h) => ({ ...h, msg: text }));
    setTimeout(() => setHud((h) => (h.over ? h : { ...h, msg: "" })), ms);
  }, []);

  const startGame = useCallback(() => {
    gv.current = {
      lane: 1,
      displayX: laneX(1),
      battery: 100,
      score: 0,
      distance: 0,
      combo: 0,
      packagesCollected: 0,
      speed: diff.speedBase,
      spawnTimer: 0,
      spawnEvery: diff.spawnBase,
      obstacles: [],
      pickups: [],
      popups: [],
      particles: [],
      lastGust: 0,
      shield: false,
      shake: 0,
      rotorAngle: 0,
      laneTimer: 0,
      startedAt: Date.now(),
    };

    overRef.current = false;
    runningRef.current = true;
    mutedRef.current = muted;
    practiceRef.current = practiceMode;
    lastHintRef.current = "";
    lastSpokenAtRef.current = 0;
    setHintText("");

    play("takeoff", mutedRef.current);

    setHud({
      score: 0,
      battery: 100,
      running: true,
      over: false,
      msg: "",
    });

    animRef.current = requestAnimationFrame(step);
  }, [difficulty, mode, diff, laneX, muted, practiceMode]);

  const endRun = useCallback(
    (reason, bonus = 0) => {
      const g = gv.current;
      cancelAnimationFrame(animRef.current);
      if (bonus) g.score += bonus;
      const finalScore = Math.floor(g.distance / 10 + g.score);
      const timeTakenSec = Math.round((Date.now() - g.startedAt) / 1000);
      overRef.current = true;
      runningRef.current = false;
      play(bonus ? "takeoff" : "gameover", mutedRef.current);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();

      if (practiceRef.current) {
        setHud((h) => ({ ...h, running: false, over: true, msg: reason + " — practice run, nothing saved." }));
        return; // never call onGameOver — no score/streak submitted
      }

      setHud((h) => ({ ...h, running: false, over: true, msg: reason }));
      onGameOver &&
        onGameOver({
          score: finalScore,
          timeTakenSec,
          packagesCollected: g.packagesCollected,
        });
    },
    [onGameOver],
  );

  function spawnObstacle() {
    const g = gv.current;
    const pool = safeMode === "conflict" ? CONFLICT_OBSTACLES : PEACE_OBSTACLES;
    const kind = pickWeighted(pool);
    let lanes = [Math.floor(Math.random() * LANES)];
    if (kind.pair) {
      const others = [0, 1, 2].filter((l) => l !== lanes[0]);
      lanes.push(others[Math.floor(Math.random() * others.length)]);
    }
    if (kind.swarm) {
      lanes = [0, 1, 2].filter(() => Math.random() < 0.75);
      if (lanes.length < 2) lanes = [0, 1, 2];
    }
    lanes.forEach((lane) => {
      g.obstacles.push({
        lane,
        y: -30,
        icon: kind.icon,
        hit: false,
        near: false,
        speedMul: kind.speedMul,
        teleLeft: kind.tele,
        big: !!kind.big,
      });
    });
  }

  function spawnPickup() {
    const g = gv.current;
    const pool = safeMode === "conflict" ? CONFLICT_PICKUPS : PEACE_PICKUPS;
    const chosen = pickWeighted(pool);
    const lane = Math.floor(Math.random() * LANES);
    g.pickups.push({
      lane,
      y: -30,
      icon: chosen.icon,
      kind: chosen.kind,
      points: chosen.points,
      color: chosen.color,
      got: false,
    });
  }

  function addPopup(x, y, text, color) {
    gv.current.popups.push({ x, y, text, color, alpha: 1, vy: -0.6 });
  }

  function step() {
    const g = gv.current;
    if (overRef.current) return;

    g.distance += g.speed;
    g.laneTimer++;

    if (g.laneTimer > diff.fatigueLimit && g.laneTimer % 30 === 0) {
      g.battery = Math.max(0, g.battery - diff.fatiguePenalty);
      play("restless", mutedRef.current);
      addPopup(
        laneX(g.lane),
        droneY - 40,
        "Restless! -" + diff.fatiguePenalty,
        "#A32D2D",
      );
    }
    g.battery = Math.max(0, g.battery - diff.drain);
    if (g.battery <= 0) {
      endRun("Out of battery");
      return;
    }

    const elapsedSec = (Date.now() - g.startedAt) / 1000;

    if (elapsedSec >= diff.sessionCapSec) {
      endRun("Mission Complete! 🎉", diff.missionBonus);
      return;
    }

    g.speed = Math.min(
      diff.speedBase + 2.3,
      diff.speedBase + elapsedSec * diff.speedRamp,
    );
    g.spawnEvery = Math.max(26, diff.spawnBase - elapsedSec * 0.5);
    g.rotorAngle += 0.9 + g.speed * 0.3;

    g.spawnTimer++;
    if (g.spawnTimer > g.spawnEvery) {
      g.spawnTimer = 0;
      spawnObstacle();
      if (Math.random() < 0.7) spawnPickup();
    }

    if (
      Date.now() - g.lastGust > 14000 + Math.random() * 4000 &&
      elapsedSec > 4
    ) {
      g.lastGust = Date.now();
      const dir = Math.random() < 0.5 ? -1 : 1;
      g.lane = Math.max(0, Math.min(LANES - 1, g.lane + dir));
      g.laneTimer = 0;
      play("gust", mutedRef.current);
      flashMsg("Wind gust!", 900);
    }

    g.displayX += (laneX(g.lane) - g.displayX) * 0.25;

    g.obstacles.forEach((o) => {
      if (o.teleLeft > 0) {
        o.teleLeft -= 1;
        return;
      }
      o.y += g.speed * o.speedMul;
    });
    g.pickups.forEach((p) => {
      p.y += g.speed;
    });
    g.popups.forEach((p) => {
      p.y += p.vy;
      p.alpha -= 0.02;
    });
    g.particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.08; // slight gravity for a nicer arc
      p.alpha -= 0.035;
    });

    g.obstacles = g.obstacles.filter((o) => o.y < H + 30);
    g.pickups = g.pickups.filter((p) => p.y < H + 30);
    g.popups = g.popups.filter((p) => p.alpha > 0);
    g.particles = g.particles.filter((p) => p.alpha > 0);

    g.obstacles.forEach((o) => {
      if (o.teleLeft > 0) return;
      const tol = o.big ? 34 : 26;
      const sameLaneClose = o.lane === g.lane && Math.abs(o.y - droneY) < tol;
      const adjacentClose =
        Math.abs(o.lane - g.lane) === 1 &&
        Math.abs(o.y - droneY) < 16 &&
        !o.near;

      if (!o.hit && sameLaneClose) {
        o.hit = true;
        if (g.shield) {
          g.shield = false;
          g.shake = 8;
          play("shieldUsed", mutedRef.current);
          addParticles(g, laneX(o.lane), droneY, "#378ADD", 10);
          addPopup(laneX(o.lane), droneY - 20, "Shield used!", "#378ADD");
        } else {
          g.combo = 0;
          g.shake = o.big ? 16 : 10;
          const pen = o.big ? diff.hitPenalty + diff.bigHitExtra : diff.hitPenalty;
          g.battery = Math.max(0, g.battery - pen);
          play("hit", mutedRef.current);
          addParticles(g, laneX(o.lane), droneY, "#E24B4A", 12);
          addPopup(laneX(o.lane), droneY - 20, "-" + pen, "#E24B4A");
          if (g.battery <= 0) {
            endRun("Crashed");
            return;
          }
        }
      } else if (!o.hit && adjacentClose) {
        o.near = true;
        g.combo += 1;
        const bonus = (o.big ? 10 : 5) + Math.min(g.combo, 10);
        g.score += bonus;
        play("nearMiss", mutedRef.current);
        addPopup(
          laneX(g.lane),
          droneY - 30,
          "Close call! +" + bonus,
          "#1D9E75",
        );
      }
    });

    g.pickups.forEach((p) => {
      if (!p.got && p.lane === g.lane && Math.abs(p.y - droneY) < 26) {
        p.got = true;
        if (p.kind === "battery") {
          g.battery = Math.min(100, g.battery + 25);
          play("battery", mutedRef.current);
          addParticles(g, laneX(p.lane), droneY, "#1D9E75", 6);
          addPopup(laneX(p.lane), droneY - 20, "+25 battery", "#1D9E75");
        } else if (p.kind === "shield" || p.kind === "radar") {
          g.shield = true;
          play("shieldUp", mutedRef.current);
          addParticles(g, laneX(p.lane), droneY, "#378ADD", 6);
          addPopup(
            laneX(p.lane),
            droneY - 20,
            p.kind === "radar" ? "Radar lock!" : "Shield up!",
            "#378ADD",
          );
        } else if (p.kind === "fact") {
          g.combo += 1;
          g.score += p.points;
          play("fact", mutedRef.current);
          addPopup(laneX(p.lane), droneY - 20, "+" + p.points, p.color);
          const pool =
            safeMode === "conflict" && Math.random() < 0.5
              ? RELIEF_MSGS
              : FACTS;
          flashMsg(pool[Math.floor(Math.random() * pool.length)], 2200);
        } else {
          g.combo += 1;
          g.packagesCollected += 1;
          const gained = p.points + g.combo * 5;
          g.score += gained;
          const soundKind = p.kind === "toffee" ? "toffee" : p.kind === "food" ? "food" : p.kind === "women" ? "women" : "agri";
          play(soundKind, mutedRef.current);
          addParticles(g, laneX(p.lane), droneY, p.color, 6);
          addPopup(laneX(p.lane), droneY - 20, "+" + gained, p.color);
          if (p.kind === "women" && Math.random() < 0.6) {
            flashMsg(
              WOMEN_MSGS[Math.floor(Math.random() * WOMEN_MSGS.length)],
              2200,
            );
          }
        }
      }
    });

    if (g.shake > 0) g.shake -= 1;

    if (practiceRef.current) {
      const hint = computeHint(g, droneY, 220);
      setHintText(hint.text);
      const now = Date.now();
      if (hint.speak !== lastHintRef.current && now - lastSpokenAtRef.current > 900) {
        speakHint(hint.speak);
        lastHintRef.current = hint.speak;
        lastSpokenAtRef.current = now;
      }
    }

    render();
    setHud((h) => ({
      ...h,
      score: Math.floor(g.distance / 10 + g.score),
      battery: g.battery,
    }));
    if (!overRef.current) animRef.current = requestAnimationFrame(step);
  }

  function drawDrone(ctx, x, y, tilt) {
    const g = gv.current;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(tilt);

    if (g.shield) {
      ctx.strokeStyle = "rgba(55,138,221,0.6)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, 0, 22, 0, Math.PI * 2);
      ctx.stroke();
    }

    const arms = [
      [-15, -9],
      [15, -9],
      [-15, 9],
      [15, 9],
    ];
    ctx.strokeStyle = "#4a4a4a";
    ctx.lineWidth = 3;
    ctx.beginPath();
    arms.forEach((p) => {
      ctx.moveTo(0, 0);
      ctx.lineTo(p[0], p[1]);
    });
    ctx.stroke();

    arms.forEach((p) => {
      ctx.save();
      ctx.translate(p[0], p[1]);
      ctx.rotate(g.rotorAngle);
      ctx.strokeStyle = "rgba(90,90,90,0.85)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-8, 0);
      ctx.lineTo(8, 0);
      ctx.moveTo(0, -8);
      ctx.lineTo(0, 8);
      ctx.stroke();
      ctx.fillStyle = "#333";
      ctx.beginPath();
      ctx.arc(0, 0, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });

    ctx.fillStyle = safeMode === "conflict" ? "#5f5e5a" : "#2b6cb0";
    ctx.fillRect(-11, -7, 22, 14);
    ctx.fillStyle = "#1a1a1a";
    ctx.beginPath();
    ctx.arc(0, 8, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = g.battery > 15 ? "#22c55e" : "#ef4444";
    ctx.beginPath();
    ctx.arc(-7, -9, 2, 0, Math.PI * 2);
    ctx.arc(7, -9, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  function render() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const g = gv.current;

    if (
      !g ||
      !Array.isArray(g.obstacles) ||
      !Array.isArray(g.pickups) ||
      !Array.isArray(g.popups)
    ) {
      return;
    }

    ctx.save();
    if (g.shake > 0)
      ctx.translate(
        (Math.random() - 0.5) * g.shake,
        (Math.random() - 0.5) * g.shake,
      );

    ctx.clearRect(-10, -10, W + 20, H + 20);
    if (safeMode === "conflict") {
      ctx.fillStyle = "rgba(90,45,45,0.06)";
      ctx.fillRect(0, 0, W, H);
    }
    for (let i = 1; i < LANES; i++) {
      ctx.strokeStyle = "rgba(128,128,128,0.25)";
      ctx.beginPath();
      ctx.moveTo(i * laneW, 0);
      ctx.lineTo(i * laneW, H);
      ctx.stroke();
    }

    ctx.strokeStyle = "rgba(120,120,120,0.35)";
    ctx.lineWidth = 2;
    for (let t = 0; t < 3; t++) {
      ctx.beginPath();
      ctx.moveTo(g.displayX - 6 + t * 4, droneY + 14 + t * 5);
      ctx.lineTo(g.displayX - 6 + t * 4, droneY + 22 + t * 5 + g.speed * 3);
      ctx.stroke();
    }
    if (g.laneTimer > diff.fatigueLimit * 0.7) {
      const warnPulse = 0.4 + 0.4 * Math.sin(Date.now() / 120);
      ctx.fillStyle = `rgba(163,45,45,${warnPulse})`;
      ctx.font = "500 12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Keep moving!", g.displayX, droneY - 50);
    }

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    g.obstacles.forEach((o) => {
      if (o.teleLeft > 0) {
        const p = 0.3 + 0.5 * Math.sin(Date.now() / 80);
        ctx.fillStyle = `rgba(163,45,45,${p})`;
        ctx.font = "500 16px sans-serif";
        ctx.fillText("⚠", laneX(o.lane), 20);
        return;
      }
      ctx.font = (o.big ? "36px" : "26px") + " sans-serif";
      ctx.fillText(o.icon, laneX(o.lane), o.y);
    });
    g.pickups.forEach((p) => {
      ctx.font = "26px sans-serif";
      if (!p.got) ctx.fillText(p.icon, laneX(p.lane), p.y);
    });

    const tilt = Math.max(
      -0.35,
      Math.min(0.35, (laneX(g.lane) - g.displayX) * -0.03),
    );
    const bob = Math.sin(Date.now() / 260) * 2.5;
    drawDrone(ctx, g.displayX, droneY + bob, tilt);

    ctx.font = "500 13px sans-serif";
    g.particles.forEach((p) => {
      ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;

    g.popups.forEach((p) => {
      ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.fillStyle = p.color;
      ctx.fillText(p.text, p.x, p.y);
    });
    ctx.globalAlpha = 1;

    if (g.battery < 15) {
      const pulse = 0.15 + 0.15 * Math.sin(Date.now() / 150);
      ctx.strokeStyle = `rgba(226,75,74,${pulse})`;
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, W - 10, H - 10);
    }
    ctx.restore();
  }

  function moveLane(dir) {
    const g = gv.current;
    if (!runningRef.current) return;
    const next = Math.max(0, Math.min(LANES - 1, g.lane + dir));
    if (next !== g.lane) g.laneTimer = 0;
    g.lane = next;
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "ArrowLeft" || e.key === "a") moveLane(-1);
      if (e.key === "ArrowRight" || e.key === "d") moveLane(1);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      cancelAnimationFrame(animRef.current);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => { render(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (runningRef.current) {
      render();
    }
  }, []);

  return (
    <div className="min-h-screen w-full bg-slate-950 flex flex-col items-center justify-start gap-3 py-4 px-3">
      <div className="w-full flex flex-col items-center justify-start gap-3">
        <div className="flex justify-between w-[320px] text-sm text-white">
          <span className="text-white/60">
            Score {practiceMode && hud.running && <span className="text-amber-400">(practice)</span>}
          </span>
          <span className="font-semibold">{hud.score}</span>
        </div>
        <div className="w-[320px] h-2.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: hud.battery + "%",
              background:
                hud.battery > 40
                  ? "#639922"
                  : hud.battery > 15
                    ? "#BA7517"
                    : "#A32D2D",
            }}
          />
        </div>

        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="w-full max-w-[320px] h-auto rounded-2xl bg-slate-900 border border-slate-800 touch-none"
          onClick={(e) => {
            const rect = e.target.getBoundingClientRect();
            moveLane(e.clientX - rect.left < W / 2 ? -1 : 1);
          }}
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            if (touchStartX.current == null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            touchStartX.current = null;
            if (Math.abs(dx) > 28) moveLane(dx > 0 ? 1 : -1); // real swipe wins over tap
          }}
        />

        {practiceMode && hud.running && (
          <p className="text-xs font-semibold text-amber-300 -mt-1">{hintText}</p>
        )}

        {!hud.over ? (
          <div className="flex gap-3">
            <button
              onClick={() => moveLane(-1)}
              className="px-5 py-2 text-sm rounded-lg bg-slate-800 text-white"
            >
              ← lane
            </button>
            <button
              onClick={() => moveLane(1)}
              className="px-5 py-2 text-sm rounded-lg bg-slate-800 text-white"
            >
              lane →
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={startGame}
              className="rounded-lg bg-emerald-600 text-white px-5 py-2 text-sm font-semibold"
            >
              Fly again
            </button>
            <button
              onClick={onExit}
              className="rounded-lg bg-slate-800 text-white px-5 py-2 text-sm"
            >
              Exit
            </button>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-2">
          {!hud.running && !hud.over && (
            <button
              onClick={startGame}
              className="rounded-lg bg-sky-500 text-white w-fit px-6 py-2 text-sm font-semibold"
            >
              Take off
            </button>
          )}

          <button
            onClick={() => setShowDroneDashHelp(true)}
            className="rounded-lg bg-sky-500 text-white w-fit px-4 py-2 text-sm font-semibold"
          >
            Help
          </button>

          <button
            onClick={() => setMuted((m) => !m)}
            className="rounded-lg bg-slate-800 text-white w-fit px-4 py-2 text-sm font-semibold"
          >
            {muted ? "🔇" : "🔊"}
          </button>

          {!hud.running && !hud.over && (
            <button
              onClick={() => setPracticeMode((p) => !p)}
              className={`rounded-lg w-fit px-4 py-2 text-sm font-semibold ${
                practiceMode ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-white"
              }`}
              title="Practice mode: spoken lane hints, score not saved"
            >
              🧪 Practice{practiceMode ? " ON" : ""}
            </button>
          )}
        </div>

        { showDroneDashHelp && (
          <DroneDashHelp onClose={() => setShowDroneDashHelp(false)} />
        )}

        <p className="text-xs text-white/60 min-h-[16px] text-center max-w-[300px]">
          {hud.msg}
        </p>
        {hud.running && (
          <button
            onClick={() => {
              cancelAnimationFrame(animRef.current);
              onExit && onExit();
            }}
            className="text-xs text-white/40 underline w-fit px-2 py-2"
          >
            End flight early
          </button>
        )}
      </div>
    </div>
  );
}
